import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";
const UPLOADS_BASE = API_BASE.startsWith("/") ? "" : API_BASE;

const DOT_GRID =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='36' height='36'%3E%3Ccircle cx='18' cy='18' r='1' fill='rgba(90,144,168,0.10)'/%3E%3C/svg%3E\")";

const TIMEZONES = [
  "Asia/Kolkata","Asia/Dubai","Asia/Singapore","Asia/Tokyo","Asia/Shanghai",
  "Asia/Bangkok","Asia/Karachi","Asia/Dhaka","Europe/London","Europe/Paris",
  "Europe/Berlin","Europe/Moscow","America/New_York","America/Chicago",
  "America/Denver","America/Los_Angeles","America/Sao_Paulo",
  "Africa/Cairo","Africa/Johannesburg","Australia/Sydney","Pacific/Auckland",
];

const GENDER_OPTIONS = [
  { label: "Male",              value: "male" },
  { label: "Female",            value: "female" },
  { label: "Non-binary",        value: "non_binary" },
  { label: "Prefer not to say", value: "prefer_not_to_say" },
];

const PRACTICE_TYPES = [
  { label: "Solo Practice",    value: "solo" },
  { label: "Clinic",           value: "clinic" },
  { label: "Hospital",         value: "hospital" },
  { label: "Telehealth Only",  value: "telehealth_only" },
];

const CONSULTATION_MODES = ["Video", "In-person", "Phone", "Chat"];

const DOCUMENT_TYPES = [
  { label: "Medical License",                 value: "medical_license" },
  { label: "Board Certificate",               value: "board_certificate" },
  { label: "Functional Medicine Certificate", value: "functional_medicine_cert" },
  { label: "Malpractice Insurance",           value: "malpractice_insurance" },
  { label: "ID Proof",                        value: "id_proof" },
  { label: "CV / Resume",                     value: "cv_resume" },
  { label: "Other",                           value: "other" },
];

/* ── Shared styles ── */
const fieldBase = (focused) => ({
  display: "block", width: "100%", padding: "12px 14px",
  border: `1px solid ${focused ? "#5A90A8" : "rgba(90,144,168,0.25)"}`,
  borderRadius: 10, fontSize: 14, fontFamily: "Inter, sans-serif",
  color: "#1C3545", background: "#F5FAFC", outline: "none",
  boxSizing: "border-box", transition: "border-color 180ms ease, box-shadow 180ms ease",
  boxShadow: focused ? "0 0 0 3px rgba(90,144,168,0.12)" : "none",
});
const textareaBase = (focused) => ({
  ...fieldBase(focused), resize: "vertical", minHeight: 90, lineHeight: 1.6,
});
const labelStyle = {
  fontSize: 13, fontWeight: 500, color: "#4D7A8E",
  fontFamily: "Inter, sans-serif", display: "block", marginBottom: 7,
};
const sectionLabel = {
  fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
  color: "#5A90A8", fontFamily: "Inter, sans-serif", marginBottom: 14,
};
const optionalTag = { marginLeft: 8, fontSize: 11, color: "#A8C8D8", fontWeight: 400 };
const divider = { borderTop: "1px solid rgba(90,144,168,0.12)", paddingTop: 24, marginBottom: 18 };

/* ── Pill ── */
function Pill({ label, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      style={{ padding: "8px 14px", borderRadius: 999, border: `1px solid ${active ? "#5A90A8" : "rgba(90,144,168,0.25)"}`, background: active ? "rgba(90,144,168,0.12)" : "transparent", color: active ? "#5A90A8" : "#7AAABB", fontSize: 13, fontWeight: active ? 600 : 400, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 180ms ease", whiteSpace: "nowrap" }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.borderColor = "#5A90A8"; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.borderColor = "rgba(90,144,168,0.25)"; }}
    >
      {label}
    </button>
  );
}

/* ── Tag input (reusable) ── */
function TagInput({ tags, onChange, placeholder, suggestions = [], disabled }) {
  const [input, setInput] = useState("");
  const [focused, setFocused] = useState(false);

  const add = (val) => {
    const v = val.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setInput("");
  };
  const remove = (tag) => onChange(tags.filter((t) => t !== tag));
  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); add(input); }
    if (e.key === "Backspace" && !input && tags.length > 0) onChange(tags.slice(0, -1));
  };

  return (
    <div>
      {tags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
          {tags.map((tag) => (
            <span key={tag} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px 5px 12px", borderRadius: 999, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.3)", color: "#5A90A8", fontSize: 13, fontFamily: "Inter, sans-serif" }}>
              {tag}
              <button type="button" onClick={() => remove(tag)} style={{ background: "none", border: "none", cursor: "pointer", color: "#7AAABB", padding: 0, lineHeight: 1 }}>✕</button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text" value={input} placeholder={placeholder} disabled={disabled}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => { add(input); setFocused(false); }}
        onFocus={() => setFocused(true)}
        style={fieldBase(focused)}
      />
      {suggestions.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {suggestions.filter((s) => !tags.includes(s)).map((s) => (
            <button key={s} type="button" onClick={() => add(s)}
              style={{ padding: "4px 10px", borderRadius: 999, border: "1px solid rgba(90,144,168,0.2)", background: "transparent", color: "#7AAABB", fontSize: 12, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#5A90A8"; e.currentTarget.style.color = "#5A90A8"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(90,144,168,0.2)"; e.currentTarget.style.color = "#7AAABB"; }}
            >+ {s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Stepper ── */
function Stepper({ current }) {
  const steps = ["Basic Profile", "Professional Profile", "Documents"];
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0, marginBottom: 36 }}>
      {steps.map((label, i) => {
        const idx = i + 1;
        const done = idx < current;
        const active = idx === current;
        return (
          <div key={label} style={{ display: "flex", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${done ? "#5A90A8" : active ? "#5A90A8" : "rgba(90,144,168,0.25)"}`, background: done ? "#5A90A8" : active ? "rgba(90,144,168,0.12)" : "transparent", transition: "all 300ms ease" }}>
                {done
                  ? <svg width="14" height="14" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  : <span style={{ fontSize: 13, fontWeight: 700, color: active ? "#5A90A8" : "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{idx}</span>
                }
              </div>
              <span style={{ fontSize: 11, fontWeight: active ? 600 : 400, color: active ? "#5A90A8" : done ? "#8AAEBB" : "#9BBCCC", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ width: 80, height: 2, background: done ? "rgba(90,144,168,0.5)" : "rgba(90,144,168,0.12)", margin: "0 8px", marginBottom: 22, transition: "background 300ms ease" }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Submit button ── */
function SubmitBtn({ canSubmit, loading, label }) {
  return (
    <button type="submit" disabled={!canSubmit}
      style={{ display: "block", width: "100%", padding: "14px", background: canSubmit ? "#5A90A8" : "rgba(90,144,168,0.25)", color: canSubmit ? "#FFFFFF" : "rgba(90,144,168,0.5)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, fontFamily: "Inter, sans-serif", cursor: canSubmit ? "pointer" : "not-allowed", letterSpacing: "0.01em", transition: "background 180ms ease, transform 180ms ease, box-shadow 180ms ease" }}
      onMouseEnter={(e) => { if (canSubmit) { e.currentTarget.style.background = "#4D7A8E"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(90,144,168,0.3)"; } }}
      onMouseLeave={(e) => { if (canSubmit) { e.currentTarget.style.background = "#5A90A8"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; } }}
    >
      {loading ? "Saving…" : label}
    </button>
  );
}

/* ════════════════════════════════════════════════════════════════
   Main Component
   ════════════════════════════════════════════════════════════════ */
export default function DoctorOnboardingPage({ doctorName = "", onFinish, onLogout }) {
  const { logout } = useAuth();
  const [step, setStep] = useState(null); // null = detecting, 1 | 2 | 3 | "success"

  /* ── Step 1 state ── */
  const [displayName, setDisplayName] = useState(doctorName);
  const [profilePhoto, setProfilePhoto] = useState("");      // URL after upload
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(""); // local object URL
  const [photoUploading, setPhotoUploading] = useState(false);
  const pendingPhotoRef = useRef(null); // holds File when doctor record doesn't exist yet
  const [phone, setPhone]             = useState("");
  const [gender, setGender]           = useState("");
  const [dob, setDob]                 = useState("");
  const [city, setCity]               = useState("");
  const [country, setCountry]         = useState("");
  const [timezone, setTimezone]       = useState("Asia/Kolkata");
  const [languages, setLanguages]     = useState([]);

  /* ── Step 2 state ── */
  const [medicalDegree, setMedicalDegree]           = useState("");
  const [additionalQuals, setAdditionalQuals]       = useState([]);
  const [primarySpecialty, setPrimarySpecialty]     = useState("");
  const [secondarySpecialties, setSecondarySpecialties] = useState([]);
  const [yearsExp, setYearsExp]                     = useState("");
  const [regNumber, setRegNumber]                   = useState("");
  const [regBody, setRegBody]                       = useState("");
  const [regCountry, setRegCountry]                 = useState("");
  const [bioShort, setBioShort]                     = useState("");
  const [bioLong, setBioLong]                       = useState("");
  const [philosophy, setPhilosophy]                 = useState("");
  const [practiceType, setPracticeType]             = useState("");
  const [clinicName, setClinicName]                 = useState("");
  const [clinicAddress, setClinicAddress]           = useState("");
  const [telehealthEnabled, setTelehealthEnabled]   = useState(true);
  const [consultationModes, setConsultationModes]   = useState([]);
  const [videoIntroUrl, setVideoIntroUrl]           = useState("");

  /* ── Step 3 state ── */
  const [documents, setDocuments]     = useState([]);
  const [docsLoaded, setDocsLoaded]   = useState(false);
  const [showDocForm, setShowDocForm]   = useState(false);
  const [docType, setDocType]           = useState("medical_license");
  const [docLabel, setDocLabel]         = useState("");
  const [docUrl, setDocUrl]             = useState("");
  const [docFileName, setDocFileName]   = useState("");
  const [docFormat, setDocFormat]       = useState("");
  const [docIssuedBy, setDocIssuedBy]   = useState("");
  const [docIssueDate, setDocIssueDate]   = useState("");
  const [docExpiryDate, setDocExpiryDate] = useState("");
  const [docLoading, setDocLoading]     = useState(false);

  /* ── focus state ── */
  const [focused, setFocused] = useState({});
  const f = (key) => ({ onFocus: () => setFocused((p) => ({ ...p, [key]: true })), onBlur: () => setFocused((p) => ({ ...p, [key]: false })) });

  /* ── async ── */
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [isInitialized, setIsInitialized] = useState(false);

  /* ── smart fill ── */
  const [scrapeUrl, setScrapeUrl]       = useState("");
  const [scrapeLoading, setScrapeLoading] = useState(false);
  const [scrapeError, setScrapeError]   = useState("");
  const [scraped, setScraped]           = useState(null);

  // On mount: fetch doctor record (and professional profile if it exists) to pre-fill all fields
  useEffect(() => {
    const init = async () => {
      try {
        const { data } = await api.get("/doctor/onboarding/me");

        // Pre-fill basic profile fields
        if (data.display_name)      setDisplayName(data.display_name);
        if (data.profile_photo_url) {
          setProfilePhoto(data.profile_photo_url);
          setProfilePhotoPreview(UPLOADS_BASE + data.profile_photo_url);
        }
        if (data.phone)             setPhone(data.phone);
        if (data.gender)            setGender(data.gender);
        if (data.date_of_birth)     setDob(data.date_of_birth);
        if (data.city)              setCity(data.city);
        if (data.country)           setCountry(data.country);
        if (data.timezone)          setTimezone(data.timezone);
        if (data.languages_spoken?.length) setLanguages(data.languages_spoken);
        setIsInitialized(true);

        const s = data.onboarding_step;

        // If professional profile was previously saved, fetch and pre-fill step 2 fields
        if (s === "documents" || s === "clusters" || s === "assessments" || s === "packages" || s === "review" || s === "live") {
          try {
            const { data: prof } = await api.get("/doctor/profile");
            if (prof.medical_degree)              setMedicalDegree(prof.medical_degree);
            if (prof.additional_qualifications?.length) setAdditionalQuals(prof.additional_qualifications);
            if (prof.primary_specialty)           setPrimarySpecialty(prof.primary_specialty);
            if (prof.secondary_specialties?.length)    setSecondarySpecialties(prof.secondary_specialties);
            if (prof.years_of_experience != null) setYearsExp(String(prof.years_of_experience));
            if (prof.registration_number)         setRegNumber(prof.registration_number);
            if (prof.registration_body)           setRegBody(prof.registration_body);
            if (prof.registration_country)        setRegCountry(prof.registration_country);
            if (prof.bio_short)                   setBioShort(prof.bio_short);
            if (prof.bio_long)                    setBioLong(prof.bio_long);
            if (prof.philosophy_statement)        setPhilosophy(prof.philosophy_statement);
            if (prof.practice_type)               setPracticeType(prof.practice_type);
            if (prof.clinic_name)                 setClinicName(prof.clinic_name);
            if (prof.clinic_address)              setClinicAddress(prof.clinic_address);
            if (prof.telehealth_enabled != null)  setTelehealthEnabled(prof.telehealth_enabled);
            if (prof.consultation_mode?.length)   setConsultationModes(prof.consultation_mode);
            if (prof.video_intro_url)             setVideoIntroUrl(prof.video_intro_url);
          } catch {
            // Professional profile fetch failed — fields stay empty, user can re-enter
          }
        }

        // Navigate to the right step
        if (s === "profile")        setStep(2);
        else if (s === "documents") setStep(3);
        else                        setStep(1); // no record or unknown step → start fresh
      } catch {
        // 404 = no doctor record yet → offer smart fill first
        setIsInitialized(false);
        setStep("smart-fill");
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load existing documents when on step 3
  useEffect(() => {
    if (step === 3 && !docsLoaded) {
      api.get("/doctor/documents").then(({ data }) => {
        setDocuments(Array.isArray(data) ? data : []);
        setDocsLoaded(true);
      }).catch((err) => {
        console.error("Failed to load documents:", err);
        setDocsLoaded(true);
      });
    }
  }, [step, docsLoaded]);

  const handleError = (err) => {
    console.error(err);
    if (!err.response) { setError(`Cannot connect to server. Make sure the backend is running on ${API_BASE}`); return; }
    const detail = err.response?.data?.detail;
    if (typeof detail === "string") setError(detail);
    else if (Array.isArray(detail)) setError(detail.map((d) => d.msg).join(", "));
    else setError(`Server error ${err.response.status}: ${err.response.statusText || "Request failed."}`);
  };

  /* ── Profile photo upload ── */
  const uploadPhotoFile = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/doctor/onboarding/upload-photo", form);
    setProfilePhoto(data.profile_photo_url);
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("Photo must be under 5 MB."); return; }
    setProfilePhotoPreview(URL.createObjectURL(file));
    if (!isInitialized) {
      // Doctor record doesn't exist yet — defer upload until after init
      pendingPhotoRef.current = file;
      return;
    }
    setPhotoUploading(true); setError("");
    try {
      await uploadPhotoFile(file);
    } catch (err) {
      handleError(err);
      setProfilePhotoPreview("");
    } finally {
      setPhotoUploading(false);
    }
  };

  /* ── Step 1 submit → POST /doctor/onboarding/init (first time) or PATCH /doctor/onboarding/me (back & re-save) ── */
  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (displayName.trim().length < 2) return;
    setError(""); setLoading(true);
    const payload = {
      display_name: displayName.trim(),
      profile_photo_url: profilePhoto || null,
      phone: phone.trim() || null,
      gender: gender || null,
      date_of_birth: dob || null,
      languages_spoken: languages,
      city: city.trim() || null,
      country: country.trim() || null,
      timezone: timezone || null,
    };
    try {
      if (isInitialized) {
        await api.patch("/doctor/onboarding/me", payload);
      } else {
        await api.post("/doctor/onboarding/init", payload);
        setIsInitialized(true);
        // Upload deferred photo now that the doctor record exists
        if (pendingPhotoRef.current) {
          try {
            setPhotoUploading(true);
            await uploadPhotoFile(pendingPhotoRef.current);
            pendingPhotoRef.current = null;
          } catch {
            // Non-fatal — profile saved, photo can be re-uploaded
          } finally {
            setPhotoUploading(false);
          }
        }
      }
      setStep(2);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 2 submit → POST /doctor/profile ── */
  const handleStep2Submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/doctor/profile", {
        medical_degree: medicalDegree.trim() || null,
        additional_qualifications: additionalQuals,
        primary_specialty: primarySpecialty.trim() || null,
        secondary_specialties: secondarySpecialties,
        years_of_experience: yearsExp !== "" ? Number(yearsExp) : null,
        registration_number: regNumber.trim() || null,
        registration_body: regBody.trim() || null,
        registration_country: regCountry.trim() || null,
        bio_short: bioShort.trim() || null,
        bio_long: bioLong.trim() || null,
        philosophy_statement: philosophy.trim() || null,
        practice_type: practiceType || null,
        clinic_name: clinicName.trim() || null,
        clinic_address: clinicAddress.trim() || null,
        telehealth_enabled: telehealthEnabled,
        consultation_mode: consultationModes,
        video_intro_url: videoIntroUrl.trim() || null,
      });
      setStep(3);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 3: upload file to server, get back file_url ── */
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError("File must be under 10 MB.");
      return;
    }
    setDocLoading(true); setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/doctor/documents/upload", formData);
      setDocUrl(data.file_url);
      setDocFileName(data.original_name);
      setDocFormat(data.file_format);
    } catch (err) {
      handleError(err);
    } finally {
      setDocLoading(false);
    }
  };

  /* ── Step 3: add a document ── */
  const handleAddDocument = async (e) => {
    e.preventDefault();
    if (!docUrl.trim()) return;
    setDocLoading(true); setError("");
    try {
      const { data } = await api.post("/doctor/documents", {
        document_type: docType,
        document_label: docLabel.trim() || null,
        file_url: docUrl.trim(),
        file_format: docFormat.trim() || null,
        issued_by: docIssuedBy.trim() || null,
        issue_date: docIssueDate || null,
        expiry_date: docExpiryDate || null,
      });
      setDocuments((prev) => [...prev, data]);
      setDocLabel(""); setDocUrl(""); setDocFileName(""); setDocFormat(""); setDocIssuedBy(""); setDocIssueDate(""); setDocExpiryDate("");
      setShowDocForm(false);
    } catch (err) {
      handleError(err);
    } finally {
      setDocLoading(false);
    }
  };

  /* ── Step 3: delete a pending document ── */
  const handleDeleteDocument = async (docId) => {
    try {
      await api.delete(`/doctor/documents/${docId}`);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (err) {
      handleError(err);
    }
  };

  /* ── Smart fill handlers ── */
  const handleScrape = async () => {
    setScrapeError("");
    setScraped(null);
    setScrapeLoading(true);
    try {
      const { data } = await api.post("/doctor/scrape", { url: scrapeUrl.trim() });
      setScraped(data);
    } catch (err) {
      setScrapeError(
        err.response?.data?.detail ||
        "Could not extract info from this URL. Please check the URL or enter details manually."
      );
    } finally {
      setScrapeLoading(false);
    }
  };

  const applyScraped = () => {
    if (!scraped) return;
    if (scraped.display_name)              setDisplayName(scraped.display_name);
    if (scraped.phone)                     setPhone(scraped.phone);
    if (scraped.city)                      setCity(scraped.city);
    if (scraped.country)                   setCountry(scraped.country);
    if (scraped.medical_degree)            setMedicalDegree(scraped.medical_degree);
    if (scraped.primary_specialty)         setPrimarySpecialty(scraped.primary_specialty);
    if (scraped.years_of_experience != null) setYearsExp(String(scraped.years_of_experience));
    if (scraped.clinic_name)               setClinicName(scraped.clinic_name);
    if (scraped.clinic_address)            setClinicAddress(scraped.clinic_address);
    if (scraped.bio_short)                 setBioShort(scraped.bio_short);
    setStep(1);
  };

  const handleLogout = async () => {
    try { await logout(); } catch { /* silent */ }
    onLogout?.();
  };

  const Bg = (
    <div aria-hidden style={{ position: "absolute", inset: 0, backgroundImage: DOT_GRID, backgroundRepeat: "repeat", pointerEvents: "none", zIndex: 1 }} />
  );

  const OnboardingHeader = (
    <header style={{ position: "relative", zIndex: 10, flexShrink: 0, background: "#8AAEBB", borderBottom: "1px solid rgba(90,144,168,0.35)", padding: "0 28px", height: 58, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontSize: 17, fontWeight: 700, color: "#FFFFFF", letterSpacing: "-0.035em", fontFamily: "Inter, sans-serif" }}>
        Supercore Longevity
      </span>
      <button
        onClick={handleLogout}
        style={{ padding: "6px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.45)", background: "transparent", color: "rgba(255,255,255,0.88)", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms ease" }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; e.currentTarget.style.color = "#FFF"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.88)"; }}
      >
        Log out
      </button>
    </header>
  );

  /* ── Loading screen (detecting onboarding step) ── */
  if (step === null) {
    return (
      <div style={{ position: "relative", minHeight: "100vh", background: "#EDF4F7", display: "flex", flexDirection: "column" }}>
        {Bg}
        {OnboardingHeader}
        <div style={{ position: "relative", zIndex: 2, flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
          <div style={{ width: 40, height: 40, border: "3px solid rgba(90,144,168,0.2)", borderTopColor: "#5A90A8", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <p style={{ color: "#7AAABB", fontSize: 14, fontFamily: "Inter, sans-serif" }}>Loading your profile…</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  /* ── Smart fill screen ── */
  if (step === "smart-fill") {
    const hasResult = scraped && scraped.fields_found > 0;
    const updateField = (field, val) => setScraped((p) => ({ ...p, [field]: val }));

    return (
      <div style={{ position: "relative", minHeight: "100vh", background: "#EDF4F7", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {Bg}
        {OnboardingHeader}
        <div style={{ position: "relative", zIndex: 2, flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", padding: "40px 24px 60px" }}>
          <div style={{ width: "100%", maxWidth: 600 }}>

            {/* Title */}
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px", borderRadius: 999, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.3)", marginBottom: 16 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#5A90A8" }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: "#5A90A8", letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "Inter, sans-serif" }}>Smart Fill</span>
              </div>
              <h1 style={{ margin: "0 0 10px", fontSize: 26, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.025em" }}>Speed up your setup</h1>
              <p style={{ margin: 0, fontSize: 14, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>
                Paste your public profile URL and we'll auto-fill your onboarding fields.
                You can review and edit everything before saving.
              </p>
            </div>

            {/* URL input card */}
            <div style={{ background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.18)", borderRadius: 18, padding: "26px 28px", marginBottom: 16, boxShadow: "0 2px 16px rgba(90,144,168,0.08)" }}>
              <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>Your profile URL</p>
              <p style={{ margin: "0 0 14px", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
                Supported: Practo · Lybrate · clinic websites · any public doctor page
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                <input
                  type="url"
                  placeholder="https://www.practo.com/doctor/..."
                  value={scrapeUrl}
                  onChange={(e) => { setScrapeUrl(e.target.value); setScrapeError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter" && scrapeUrl.trim()) handleScrape(); }}
                  style={{ flex: 1, padding: "12px 14px", border: `1px solid ${scrapeError ? "rgba(220,38,38,0.4)" : "rgba(90,144,168,0.25)"}`, borderRadius: 10, fontSize: 14, fontFamily: "Inter, sans-serif", color: "#1C3545", background: "#F5FAFC", outline: "none", boxSizing: "border-box" }}
                  disabled={scrapeLoading}
                />
                <button
                  type="button"
                  onClick={handleScrape}
                  disabled={!scrapeUrl.trim() || scrapeLoading}
                  style={{ flexShrink: 0, padding: "12px 22px", background: scrapeUrl.trim() && !scrapeLoading ? "#5A90A8" : "rgba(90,144,168,0.25)", color: scrapeUrl.trim() && !scrapeLoading ? "#FFFFFF" : "rgba(90,144,168,0.5)", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, fontFamily: "Inter, sans-serif", cursor: scrapeUrl.trim() && !scrapeLoading ? "pointer" : "not-allowed", transition: "all 180ms ease", whiteSpace: "nowrap" }}
                >
                  {scrapeLoading ? "Extracting…" : "Extract Info"}
                </button>
              </div>
              {scrapeError && (
                <p style={{ margin: "10px 0 0", fontSize: 13, color: "#DC2626", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>{scrapeError}</p>
              )}
            </div>

            {/* Extracted results */}
            {hasResult && (
              <div style={{ background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.2)", borderRadius: 18, padding: "26px 28px", marginBottom: 16, boxShadow: "0 2px 16px rgba(90,144,168,0.08)" }}>
                {/* Banner */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22, padding: "10px 14px", borderRadius: 10, background: "rgba(22,163,74,0.06)", border: "1px solid rgba(22,163,74,0.2)" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#16A34A", fontFamily: "Inter, sans-serif" }}>
                    Found {scraped.fields_found} field{scraped.fields_found !== 1 ? "s" : ""}
                  </span>
                  <span style={{ fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", marginLeft: 4 }}>
                    · Review and edit before continuing
                  </span>
                </div>

                {/* Editable fields grid */}
                <div className="ob-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
                  {[
                    { label: "Full Name",        field: "display_name",       span: 2 },
                    { label: "Primary Specialty", field: "primary_specialty",  span: 2 },
                    { label: "Medical Degree",    field: "medical_degree",     span: 1 },
                    { label: "Years Experience",  field: "years_of_experience", span: 1, type: "number" },
                    { label: "Phone",             field: "phone",              span: 1 },
                    { label: "City",              field: "city",               span: 1 },
                    { label: "Country",           field: "country",            span: 1 },
                    { label: "Clinic / Hospital", field: "clinic_name",        span: 1 },
                    { label: "Address",           field: "clinic_address",     span: 2 },
                  ].map(({ label, field, span, type = "text" }) => (
                    <div key={field} style={{ gridColumn: `span ${span}` }}>
                      <p style={{ margin: "0 0 5px", fontSize: 11, fontWeight: 600, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</p>
                      <input
                        type={type}
                        value={scraped[field] ?? ""}
                        onChange={(e) => updateField(field, type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
                        style={{ display: "block", width: "100%", padding: "10px 12px", border: "1px solid rgba(90,144,168,0.2)", borderRadius: 8, fontSize: 13, fontFamily: "Inter, sans-serif", color: scraped[field] ? "#1C3545" : "#A8C8D8", background: scraped[field] ? "rgba(90,144,168,0.04)" : "#F5FAFC", outline: "none", boxSizing: "border-box" }}
                        placeholder={`No ${label.toLowerCase()} found`}
                      />
                    </div>
                  ))}
                </div>

                {/* Bio */}
                {(scraped.bio_short !== null && scraped.bio_short !== undefined) && (
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ margin: "0 0 5px", fontSize: 11, fontWeight: 600, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.07em" }}>Short Bio</p>
                    <textarea
                      value={scraped.bio_short ?? ""}
                      onChange={(e) => updateField("bio_short", e.target.value)}
                      rows={3}
                      style={{ display: "block", width: "100%", padding: "10px 12px", border: "1px solid rgba(90,144,168,0.2)", borderRadius: 8, fontSize: 13, fontFamily: "Inter, sans-serif", color: scraped.bio_short ? "#1C3545" : "#A8C8D8", background: scraped.bio_short ? "rgba(90,144,168,0.04)" : "#F5FAFC", outline: "none", boxSizing: "border-box", resize: "vertical", lineHeight: 1.6 }}
                      placeholder="No bio found"
                    />
                  </div>
                )}

                <button
                  type="button"
                  onClick={applyScraped}
                  style={{ display: "block", width: "100%", padding: "14px", background: "#5A90A8", color: "#FFFFFF", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "background 180ms ease, transform 180ms ease, box-shadow 180ms ease" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#4D7A8E"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(90,144,168,0.3)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#5A90A8"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                >
                  Use this info &amp; continue →
                </button>
              </div>
            )}

            {/* Empty result notice */}
            {scraped && scraped.fields_found === 0 && (
              <div style={{ padding: "16px 18px", borderRadius: 12, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", marginBottom: 16 }}>
                <p style={{ margin: 0, fontSize: 13, color: "#DC2626", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>
                  No structured data was found on this page. The site may not expose machine-readable profile info.
                  Please enter your details manually below.
                </p>
              </div>
            )}

            {/* Manual option */}
            <div style={{ textAlign: "center", paddingTop: 8 }}>
              <button
                type="button"
                onClick={() => setStep(1)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textDecoration: "underline", textUnderlineOffset: 3 }}
                onMouseEnter={(e) => e.currentTarget.style.color = "#1C3545"}
                onMouseLeave={(e) => e.currentTarget.style.color = "#9BBCCC"}
              >
                Skip — I'll enter my details manually
              </button>
            </div>

          </div>
        </div>
      </div>
    );
  }

  /* ── Success screen ── */
  if (step === "success") {
    return (
      <div style={{ position: "relative", minHeight: "100vh", background: "#EDF4F7", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {Bg}
        {OnboardingHeader}
        <div style={{ position: "relative", zIndex: 2, flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 24px" }}>
        <div style={{ background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.2)", borderRadius: 22, padding: "48px 40px", width: "100%", maxWidth: 460, textAlign: "center", boxShadow: "0 4px 32px rgba(90,144,168,0.12)" }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(22,163,74,0.1)", border: "2px solid rgba(22,163,74,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <h1 style={{ margin: "0 0 12px", fontSize: 22, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>Onboarding Complete!</h1>
          <p style={{ margin: "0 0 32px", fontSize: 14, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>
            Your profile and professional details have been saved. Our team will review your application and get back to you shortly.
          </p>
          <button type="button" onClick={onFinish}
            style={{ padding: "13px 32px", background: "#5A90A8", color: "#FFFFFF", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
            Go to Dashboard
          </button>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "relative", minHeight: "100vh", background: "#EDF4F7", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {Bg}
      {OnboardingHeader}

      <div style={{ position: "relative", zIndex: 2, flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", padding: "40px 24px 60px" }}>
      <div style={{ width: "100%", maxWidth: 580 }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px", borderRadius: 999, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.3)", marginBottom: 16 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#5A90A8" }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: "#5A90A8", letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "Inter, sans-serif" }}>Doctor Onboarding</span>
          </div>
          <h1 style={{ margin: "0 0 8px", fontSize: 26, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.025em" }}>
            {step === 1 ? "Complete your profile" : step === 2 ? "Professional details" : "Upload Documents"}
          </h1>
          <p style={{ margin: 0, fontSize: 14, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
            {step === 1 ? "Tell us about yourself to set up your doctor account." : step === 2 ? "Help patients and our team understand your expertise." : "Upload your credentials and verification documents."}
          </p>
        </div>

        {/* Stepper */}
        <Stepper current={step} />

        {/* Card */}
        <div style={{ background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.18)", borderRadius: 22, padding: "36px", boxShadow: "0 4px 32px rgba(90,144,168,0.12)" }}>

          {/* Error banner */}
          {error && (
            <div style={{ marginBottom: 24, padding: "11px 14px", borderRadius: 9, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 13, fontFamily: "Inter, sans-serif", lineHeight: 1.45 }}>
              {error}
            </div>
          )}

          {/* ════════════ STEP 1 ════════════ */}
          {step === 1 && (
            <form onSubmit={handleStep1Submit}>

              {/* Profile photo */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 28 }}>
                <label style={{ cursor: photoUploading ? "not-allowed" : "pointer", position: "relative" }}>
                  <input type="file" accept=".jpg,.jpeg,.png" onChange={handlePhotoSelect} disabled={photoUploading} style={{ display: "none" }} />
                  <div style={{ width: 88, height: 88, borderRadius: "50%", border: `2px dashed ${profilePhotoPreview ? "#5A90A8" : "rgba(90,144,168,0.3)"}`, background: profilePhotoPreview ? "transparent" : "rgba(90,144,168,0.04)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", transition: "border-color 180ms ease", position: "relative" }}>
                    {profilePhotoPreview
                      ? <img src={profilePhotoPreview} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(90,144,168,0.4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                    }
                    <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "rgba(90,144,168,0.3)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 180ms ease" }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = "0"}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    </div>
                  </div>
                </label>
                <p style={{ margin: "8px 0 0", fontSize: 12, color: photoUploading ? "#5A90A8" : "#7AAABB", fontFamily: "Inter, sans-serif" }}>
                  {photoUploading ? "Uploading…" : profilePhotoPreview ? "Click to change photo" : "Upload profile photo"}
                </p>
                <p style={{ margin: "2px 0 0", fontSize: 11, color: "#A8C8D8", fontFamily: "Inter, sans-serif" }}>jpg, png · max 5 MB</p>
              </div>

              <p style={sectionLabel}>Identity</p>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Display name <span style={{ color: "#5A90A8" }}>*</span><span style={optionalTag}>min 2 characters</span></label>
                <input type="text" placeholder="Dr. Jane Smith" value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={fieldBase(focused.dn)} {...f("dn")} disabled={loading} />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Phone number<span style={optionalTag}>optional</span></label>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", padding: "12px", background: "#F5FAFC", border: "1px solid rgba(90,144,168,0.25)", borderRadius: 10, color: "#4D7A8E", fontSize: 13, fontFamily: "Inter, sans-serif", whiteSpace: "nowrap", flexShrink: 0 }}>🇮🇳 +91</div>
                  <input type="tel" placeholder="9876543210" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} style={{ ...fieldBase(focused.ph), flex: 1 }} {...f("ph")} disabled={loading} />
                </div>
              </div>

              <div style={divider}><p style={sectionLabel}>Personal</p></div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Gender<span style={optionalTag}>optional</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {GENDER_OPTIONS.map(({ label, value }) => (
                    <Pill key={value} label={label} active={gender === value} onClick={() => setGender(gender === value ? "" : value)} />
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Date of birth<span style={optionalTag}>optional</span></label>
                <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} max={new Date().toISOString().split("T")[0]} style={{ ...fieldBase(false), colorScheme: "light" }} disabled={loading} />
              </div>

              <div style={divider}><p style={sectionLabel}>Location</p></div>

              <div className="ob-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
                <div>
                  <label style={labelStyle}>City</label>
                  <input type="text" placeholder="Mumbai" value={city} onChange={(e) => setCity(e.target.value)} style={fieldBase(focused.city)} {...f("city")} disabled={loading} />
                </div>
                <div>
                  <label style={labelStyle}>Country</label>
                  <input type="text" placeholder="India" value={country} onChange={(e) => setCountry(e.target.value)} style={fieldBase(focused.country)} {...f("country")} disabled={loading} />
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Timezone</label>
                <select value={timezone} onChange={(e) => setTimezone(e.target.value)} style={{ ...fieldBase(false), cursor: "pointer" }} disabled={loading}>
                  {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>

              <div style={divider}><p style={sectionLabel}>Languages Spoken</p></div>

              <div style={{ marginBottom: 32 }}>
                <label style={{ ...labelStyle, marginBottom: 10 }}>Languages<span style={optionalTag}>type and press Enter</span></label>
                <TagInput tags={languages} onChange={setLanguages} placeholder="e.g. English, Hindi, Tamil…" disabled={loading} />
              </div>

              <SubmitBtn canSubmit={!loading && displayName.trim().length >= 2} loading={loading} label="Save & Continue →" />
            </form>
          )}

          {/* ════════════ STEP 2 ════════════ */}
          {step === 2 && (
            <form onSubmit={handleStep2Submit}>
              <p style={sectionLabel}>Qualifications</p>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Medical degree<span style={optionalTag}>optional</span></label>
                <input type="text" placeholder="MBBS, MD, DO…" value={medicalDegree} onChange={(e) => setMedicalDegree(e.target.value)} style={fieldBase(focused.deg)} {...f("deg")} disabled={loading} />
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ ...labelStyle, marginBottom: 10 }}>Additional qualifications<span style={optionalTag}>type and press Enter</span></label>
                <TagInput tags={additionalQuals} onChange={setAdditionalQuals} placeholder="e.g. Fellowship in Longevity Medicine…" disabled={loading} />
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Primary specialty<span style={optionalTag}>optional</span></label>
                <input type="text" placeholder="e.g. Functional Medicine" value={primarySpecialty} onChange={(e) => setPrimarySpecialty(e.target.value)} style={fieldBase(focused.pspec)} {...f("pspec")} disabled={loading} />
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ ...labelStyle, marginBottom: 10 }}>Secondary specialties<span style={optionalTag}>type and press Enter</span></label>
                <TagInput tags={secondarySpecialties} onChange={setSecondarySpecialties} placeholder="e.g. Sports Medicine, Cardiology…" disabled={loading} />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Years of experience<span style={optionalTag}>0–70</span></label>
                <input type="number" min={0} max={70} placeholder="e.g. 10" value={yearsExp} onChange={(e) => setYearsExp(e.target.value)} style={fieldBase(focused.exp)} {...f("exp")} disabled={loading} />
              </div>

              <div style={divider}><p style={sectionLabel}>Registration</p></div>

              <div className="ob-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
                <div>
                  <label style={labelStyle}>Registration number</label>
                  <input type="text" placeholder="MCI-12345" value={regNumber} onChange={(e) => setRegNumber(e.target.value)} style={fieldBase(focused.rn)} {...f("rn")} disabled={loading} />
                </div>
                <div>
                  <label style={labelStyle}>Issuing body</label>
                  <input type="text" placeholder="Medical Council of India" value={regBody} onChange={(e) => setRegBody(e.target.value)} style={fieldBase(focused.rb)} {...f("rb")} disabled={loading} />
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Registration country</label>
                <input type="text" placeholder="India" value={regCountry} onChange={(e) => setRegCountry(e.target.value)} style={fieldBase(focused.rc)} {...f("rc")} disabled={loading} />
              </div>

              <div style={divider}><p style={sectionLabel}>About You</p></div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>
                  Short bio<span style={optionalTag}>max 280 chars · shown to patients</span>
                </label>
                <textarea value={bioShort} onChange={(e) => setBioShort(e.target.value.slice(0, 280))} placeholder="A brief description of who you are and what you offer…" style={textareaBase(focused.bs)} {...f("bs")} disabled={loading} />
                <div style={{ textAlign: "right", fontSize: 11, color: "#A8C8D8", marginTop: 4, fontFamily: "Inter, sans-serif" }}>{bioShort.length}/280</div>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Full bio<span style={optionalTag}>optional · detailed background</span></label>
                <textarea value={bioLong} onChange={(e) => setBioLong(e.target.value)} placeholder="Your full professional background, research, publications…" style={{ ...textareaBase(focused.bl), minHeight: 120 }} {...f("bl")} disabled={loading} />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Philosophy statement<span style={optionalTag}>optional</span></label>
                <textarea value={philosophy} onChange={(e) => setPhilosophy(e.target.value)} placeholder="Your approach to medicine and patient care…" style={textareaBase(focused.ph2)} {...f("ph2")} disabled={loading} />
              </div>

              <div style={divider}><p style={sectionLabel}>Practice Details</p></div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Practice type<span style={optionalTag}>optional</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {PRACTICE_TYPES.map(({ label, value }) => (
                    <Pill key={value} label={label} active={practiceType === value} onClick={() => setPracticeType(practiceType === value ? "" : value)} />
                  ))}
                </div>
              </div>

              <div className="ob-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
                <div>
                  <label style={labelStyle}>Clinic / hospital name</label>
                  <input type="text" placeholder="Apollo Clinic" value={clinicName} onChange={(e) => setClinicName(e.target.value)} style={fieldBase(focused.cn)} {...f("cn")} disabled={loading} />
                </div>
                <div>
                  <label style={labelStyle}>Video intro URL</label>
                  <input type="url" placeholder="https://youtube.com/…" value={videoIntroUrl} onChange={(e) => setVideoIntroUrl(e.target.value)} style={fieldBase(focused.vu)} {...f("vu")} disabled={loading} />
                </div>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Clinic address<span style={optionalTag}>optional</span></label>
                <textarea value={clinicAddress} onChange={(e) => setClinicAddress(e.target.value)} placeholder="123 Health Street, Mumbai 400001…" style={textareaBase(focused.ca)} {...f("ca")} disabled={loading} />
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ ...labelStyle, marginBottom: 10 }}>Consultation modes<span style={optionalTag}>select or add custom</span></label>
                <TagInput tags={consultationModes} onChange={setConsultationModes} placeholder="Add a mode…" suggestions={CONSULTATION_MODES} disabled={loading} />
              </div>

              <div style={{ marginBottom: 32 }}>
                <div
                  onClick={() => setTelehealthEnabled((v) => !v)}
                  style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px", borderRadius: 12, border: `1px solid ${telehealthEnabled ? "rgba(90,144,168,0.5)" : "rgba(90,144,168,0.18)"}`, background: telehealthEnabled ? "rgba(90,144,168,0.07)" : "rgba(90,144,168,0.02)", cursor: "pointer", transition: "border-color 180ms, background 180ms" }}
                  role="checkbox" aria-checked={telehealthEnabled} tabIndex={0}
                  onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); setTelehealthEnabled((v) => !v); } }}
                >
                  <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1, border: `2px solid ${telehealthEnabled ? "#5A90A8" : "rgba(90,144,168,0.35)"}`, background: telehealthEnabled ? "#5A90A8" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 150ms ease" }}>
                    {telehealthEnabled && <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden><polyline points="2,6 5,9 10,3" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: telehealthEnabled ? "#5A90A8" : "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.3, transition: "color 150ms" }}>Telehealth enabled</p>
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>I am available for remote / online consultations.</p>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <button type="button" onClick={() => { setError(""); setStep(1); }}
                  style={{ flex: "0 0 auto", padding: "14px 20px", background: "rgba(90,144,168,0.06)", color: "#7AAABB", border: "1px solid rgba(90,144,168,0.2)", borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#5A90A8"; e.currentTarget.style.color = "#1C3545"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(90,144,168,0.2)"; e.currentTarget.style.color = "#7AAABB"; }}
                >← Back</button>
                <div style={{ flex: 1 }}>
                  <SubmitBtn canSubmit={!loading} loading={loading} label="Save & Continue →" />
                </div>
              </div>
            </form>
          )}

          {/* ════════════ STEP 3 ════════════ */}
          {step === 3 && (
            <div>
              {/* Existing documents list */}
              {documents.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <p style={sectionLabel}>Uploaded Documents</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {documents.map((doc) => {
                      const typeLabel = DOCUMENT_TYPES.find((t) => t.value === doc.document_type)?.label || doc.document_type;
                      const isPending = doc.verification_status === "pending";
                      return (
                        <div key={doc.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 16px", borderRadius: 12, border: "1px solid rgba(90,144,168,0.15)", background: "#F5FAFC" }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{doc.document_label || typeLabel}</p>
                            <p style={{ margin: "3px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{typeLabel}</p>
                          </div>
                          <span style={{ flexShrink: 0, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", background: doc.verification_status === "verified" ? "rgba(22,163,74,0.1)" : doc.verification_status === "rejected" ? "rgba(220,38,38,0.08)" : "rgba(90,144,168,0.08)", color: doc.verification_status === "verified" ? "#16A34A" : doc.verification_status === "rejected" ? "#DC2626" : "#7AAABB", border: `1px solid ${doc.verification_status === "verified" ? "rgba(22,163,74,0.25)" : doc.verification_status === "rejected" ? "rgba(220,38,38,0.25)" : "rgba(90,144,168,0.2)"}` }}>
                            {doc.verification_status}
                          </span>
                          {isPending && (
                            <button type="button" onClick={() => handleDeleteDocument(doc.id)}
                              style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(220,38,38,0.25)", background: "rgba(220,38,38,0.06)", color: "#DC2626", fontSize: 12, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(220,38,38,0.12)"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(220,38,38,0.06)"; }}
                            >Remove</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Add document form */}
              {showDocForm ? (
                <form onSubmit={handleAddDocument} style={{ padding: "20px", borderRadius: 14, border: "1px solid rgba(90,144,168,0.2)", background: "rgba(90,144,168,0.04)", marginBottom: 20 }}>
                  <p style={{ ...sectionLabel, marginBottom: 16 }}>Add Document</p>

                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Document type <span style={{ color: "#5A90A8" }}>*</span></label>
                    <select value={docType} onChange={(e) => setDocType(e.target.value)} style={{ ...fieldBase(false), cursor: "pointer" }} disabled={docLoading}>
                      {DOCUMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Label <span style={optionalTag}>optional — e.g. "MCI Certificate 2024"</span></label>
                    <input type="text" placeholder="Custom label" value={docLabel} onChange={(e) => setDocLabel(e.target.value)} style={fieldBase(focused.dlabel)} {...f("dlabel")} disabled={docLoading} />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>File <span style={{ color: "#5A90A8" }}>*</span><span style={optionalTag}>pdf, jpg, png, doc, docx — max 10 MB</span></label>
                    <label style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", border: `1px solid ${docUrl ? "#5A90A8" : "rgba(90,144,168,0.25)"}`, borderRadius: 10, background: "#F5FAFC", cursor: docLoading ? "not-allowed" : "pointer", transition: "border-color 180ms ease" }}>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleFileSelect} disabled={docLoading} style={{ display: "none" }} />
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={docUrl ? "#5A90A8" : "#9BBCCC"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12V14h12v-2"/><path d="M8 2v9"/><polyline points="4,6 8,2 12,6"/></svg>
                      <span style={{ fontSize: 13, fontFamily: "Inter, sans-serif", color: docUrl ? "#5A90A8" : "#9BBCCC", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {docLoading ? "Uploading…" : docFileName || "Choose file to upload"}
                      </span>
                      {docUrl && <svg width="14" height="14" viewBox="0 0 12 12" fill="none" stroke="#16A34A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="2,6 5,9 10,3"/></svg>}
                    </label>
                  </div>

                  <div className="ob-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
                    <div>
                      <label style={labelStyle}>Format<span style={optionalTag}>pdf / jpg / png</span></label>
                      <input type="text" placeholder="pdf" value={docFormat} onChange={(e) => setDocFormat(e.target.value.slice(0, 10))} style={fieldBase(focused.dfmt)} {...f("dfmt")} disabled={docLoading} />
                    </div>
                    <div>
                      <label style={labelStyle}>Issued by<span style={optionalTag}>optional</span></label>
                      <input type="text" placeholder="Medical Council of India" value={docIssuedBy} onChange={(e) => setDocIssuedBy(e.target.value)} style={fieldBase(focused.dissby)} {...f("dissby")} disabled={docLoading} />
                    </div>
                  </div>

                  <div className="ob-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
                    <div>
                      <label style={labelStyle}>Issue date<span style={optionalTag}>optional</span></label>
                      <input type="date" value={docIssueDate} onChange={(e) => setDocIssueDate(e.target.value)} style={{ ...fieldBase(false), colorScheme: "light" }} disabled={docLoading} />
                    </div>
                    <div>
                      <label style={labelStyle}>Expiry date<span style={optionalTag}>optional</span></label>
                      <input type="date" value={docExpiryDate} onChange={(e) => setDocExpiryDate(e.target.value)} style={{ ...fieldBase(false), colorScheme: "light" }} disabled={docLoading} />
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 10 }}>
                    <button type="button" onClick={() => setShowDocForm(false)}
                      style={{ flex: "0 0 auto", padding: "11px 18px", background: "rgba(90,144,168,0.06)", color: "#7AAABB", border: "1px solid rgba(90,144,168,0.2)", borderRadius: 9, fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
                      Cancel
                    </button>
                    <button type="submit" disabled={!docUrl.trim() || docLoading}
                      style={{ flex: 1, padding: "11px", background: !docUrl.trim() || docLoading ? "rgba(90,144,168,0.25)" : "#5A90A8", color: !docUrl.trim() || docLoading ? "rgba(90,144,168,0.5)" : "#FFFFFF", border: "none", borderRadius: 9, fontSize: 14, fontWeight: 700, fontFamily: "Inter, sans-serif", cursor: !docUrl.trim() || docLoading ? "not-allowed" : "pointer" }}>
                      {docLoading ? "Uploading…" : "Add Document"}
                    </button>
                  </div>
                </form>
              ) : (
                <button type="button" onClick={() => setShowDocForm(true)}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "13px", borderRadius: 12, border: "1px dashed rgba(90,144,168,0.4)", background: "rgba(90,144,168,0.04)", color: "#5A90A8", fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", marginBottom: 24, transition: "all 180ms ease" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(90,144,168,0.08)"; e.currentTarget.style.borderColor = "#5A90A8"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(90,144,168,0.04)"; e.currentTarget.style.borderColor = "rgba(90,144,168,0.4)"; }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#5A90A8" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="2" x2="8" y2="14"/><line x1="2" y1="8" x2="14" y2="8"/></svg>
                  Add Document
                </button>
              )}

              {documents.length === 0 && !showDocForm && (
                <p style={{ margin: "0 0 20px", fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif", textAlign: "center", lineHeight: 1.6 }}>
                  No documents added yet. Upload your license, certificate, or other credentials above.
                </p>
              )}

              <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                <button type="button" onClick={() => { setError(""); setStep(2); }}
                  style={{ flex: "0 0 auto", padding: "14px 20px", background: "rgba(90,144,168,0.06)", color: "#7AAABB", border: "1px solid rgba(90,144,168,0.2)", borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#5A90A8"; e.currentTarget.style.color = "#1C3545"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(90,144,168,0.2)"; e.currentTarget.style.color = "#7AAABB"; }}
                >← Back</button>
                <button type="button" onClick={() => setStep("success")}
                  style={{ flex: 1, padding: "14px", background: "#5A90A8", color: "#FFFFFF", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "background 180ms ease, transform 180ms ease, box-shadow 180ms ease" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#4D7A8E"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(90,144,168,0.3)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#5A90A8"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                >
                  Complete Setup ✓
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
      </div>
    </div>
  );
}
